import { Button, ButtonGroup, Checkbox, MenuItem, Select } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    activityAutocomplete,
    agentAutocomplete,
    bookingStatusAutocomplete,
    clientAutocomplete,
    logTypeAutocomplete,
    paymentMethodAutocomplete,
    userAutocomplete
} from '../../../autocomplete';
import { TimesIcon } from '../../../icons';
import AutocompleteSelect from '../../atom/AutocompleteSelect';
import FilterRangePicker from '../../atom/FilterRangePicker';
import FilterPopupMenu from './FilterPopupMenu';
import TextFieldAutoWidth from './TextFieldAutoWidth';
import DatePickerDropdown from '../../atom/DatePickerDropdown';

const AUTOCOMPLETE_MAPS = {
    activityautocomplete: {
        initCall: activityAutocomplete.init,
        autocompleteCall: activityAutocomplete.search
    },
    agentautocomplete: {
        initCall: agentAutocomplete.init,
        autocompleteCall: agentAutocomplete.search
    },
    vendorautocomplete: {
        initCall: clientAutocomplete.init,
        autocompleteCall: clientAutocomplete.search
    },
    guestautocomplete: {
        initCall: userAutocomplete.init,
        autocompleteCall: userAutocomplete.search,
        InputProps: { style: { minWidth: 325 } }
    },
    paymentmethodautocomplete: {
        initCall: paymentMethodAutocomplete.init,
        autocompleteCall: paymentMethodAutocomplete.search
    },
    logtypeautocomplete: {
        initCall: logTypeAutocomplete.init,
        autocompleteCall: logTypeAutocomplete.search,
        InputProps: { style: { minWidth: 300 } }
    },
    statuslistautocomplete: {
        initCall: bookingStatusAutocomplete.init,
        autocompleteCall: bookingStatusAutocomplete.search,
        multiple: true,
        InputProps: { style: { width: 'auto', minWidth: 280 } }
    }
};

const SELECT_MAPS = {
    statusSelect: {
        options: [
            { value: 'all', name: 'All' },
            { value: 'unconfirmed', name: 'Unconfirmed' },
            { value: 'confirmed', name: 'Confirmed' },
            { value: 'denied', name: 'Denied' },
            // no need to change cancelled here, we filter on backend side
            { value: 'cancelled', name: 'Cancelled' }
        ]
    },
    resultSelect: {
        options: [
            { value: 'all', name: 'All' },
            { value: 'success', name: 'Success' },
            { value: 'created', name: 'Created' },
            { value: 'error', name: 'Error' }
        ]
    },
    expiringSelect: {
        options: [
            { value: '30', name: '30 days' },
            { value: '60', name: '60 days' },
            { value: '90', name: '90 days' }
        ]
    },
    emailStatusSelect: {
        multiple: true,
        options: [
            { value: 'queued', name: 'Queued' },
            { value: 'delivered', name: 'Delivered' },
            { value: 'bounce', name: 'Bounce' },
            { value: 'dropped', name: 'Dropped' },
            { value: 'spamreport', name: 'Spam Report' }
        ]
    },
    emailLogTypeSelect: {
        multiple: true,
        options: [
            { value: 'sms', name: 'SMS' },
            { value: 'email', name: 'Email' }
        ]
    }
};

const FilterItem = function({ type, value, setValue }) {
    // handle autocomplets in one run
    if (AUTOCOMPLETE_MAPS[type]) {
        const isMultiple = AUTOCOMPLETE_MAPS[type].multiple || false;

        return (
            <AutocompleteSelect
                {...AUTOCOMPLETE_MAPS[type]}
                value={isMultiple ? value || [] : value || null}
                setValue={setValue}
                multiple={isMultiple}
            >
                <TextFieldAutoWidth />
            </AutocompleteSelect>
        );
    }

    if (type === 'daterange') {
        return (
            <FilterRangePicker
                disablePast={false}
                filters={value}
                setFilters={setValue}
                mask="MM-DD-yyyy"
                fromKey="0"
                toKey="1"
                inlineVariant
            />
        );
    }

    if (type === 'date') {
        return (
            <DatePickerDropdown
                disablePast={false}
                date={value}
                setDate={setValue}
                inputFormat="YYYY-MM-DD"
                calendarIcon={false}
            />
        );
    }

    if (type === 'datetime') {
        return (
            <DatePickerDropdown
                disablePast={false}
                date={value}
                setDate={setValue}
                inputFormat="YYYY-MM-DD HH:mm"
                calendarIcon={false}
                enableTimePicker
            />
        );
    }

    if (SELECT_MAPS[type]) {
        const isMultiple = SELECT_MAPS[type].multiple || false;
        const selectValue = isMultiple
            ? Array.isArray(value)
                ? value
                : value
                ? value.split(',')
                : []
            : value;

        return (
            <Select
                value={selectValue}
                multiple={isMultiple}
                onChange={event => {
                    const val = event.target.value;
                    if (isMultiple) {
                        setValue(Array.isArray(val) ? val.join(',') : val);
                    } else {
                        setValue(val);
                    }
                }}
                renderValue={
                    isMultiple
                        ? selected => {
                              if (!selected || selected.length === 0) {
                                  return 'All';
                              }
                              return selected
                                  .map(v => {
                                      const opt = SELECT_MAPS[type].options.find(
                                          o => o.value === v
                                      );
                                      return opt ? opt.name : v;
                                  })
                                  .join(', ');
                          }
                        : undefined
                }
                displayEmpty={isMultiple}
                style={isMultiple ? { minWidth: 120 } : undefined}
                MenuProps={
                    isMultiple
                        ? {
                              variant: 'menu',
                              getContentAnchorEl: null,
                              anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
                          }
                        : undefined
                }
            >
                {SELECT_MAPS[type].options.map(e => (
                    <MenuItem key={e.value} value={e.value}>
                        {isMultiple && (
                            <Checkbox
                                checked={selectValue.indexOf(e.value) > -1}
                                size="small"
                                style={{ padding: '0 8px 0 0' }}
                            />
                        )}
                        {e.name}
                    </MenuItem>
                ))}
            </Select>
        );
    }

    // everything else is a simple textfield
    return (
        <TextFieldAutoWidth
            inputProps={{ value }}
            onChange={event => setValue(event.target.value)}
        />
    );
};

const FilterMenu = ({ item, setItem, clearItem, options, handleOptionClick }) => {
    const filterRef = useRef(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (open === true) {
            return;
        }

        filterRef.current.focus();
    }, [open]);

    const handleToggle = useCallback(() => {
        setOpen(o => !o);
    }, [setOpen]);

    return (
        <>
            <ButtonGroup size="small" disableFocusRipple disableRipple className="searchfilter">
                <Button
                    ref={filterRef}
                    aria-controls={open ? 'menu-list-grow2' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                >
                    {item.label}
                </Button>
                {/* button inside another button: Force component rendering */}
                <Button component="div">
                    <FilterItem
                        type={item.type}
                        value={item.value}
                        setValue={value => setItem({ ...item, value })}
                    />
                </Button>
                <Button onClick={() => clearItem({ ...item, value: '' })}>
                    <TimesIcon style={{ fontSize: '80%' }} />
                </Button>
            </ButtonGroup>
            <FilterPopupMenu
                open={open}
                anchorRef={filterRef}
                setOpen={setOpen}
                options={options}
                handleOptionClick={option => handleOptionClick(option)}
            />
        </>
    );
};

export default FilterMenu;
